import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Moment from "moment";
import { ColorRing } from 'react-loader-spinner';
import { useGetShapeSizeDistributionQuery, useGetTableDistributionQuery } from '../../redux/slices/apiSlice';
import { pricing, setPreDefinedSearchId, setPreDefinedSort } from '../../redux/slices/pricingSlice';
import { selectDateRange, setDateRange } from '../../redux/slices/dateRangeSlice';
import ShapeCaratGrid from '../shapeCaratGrid';
import debounce from 'lodash/debounce';
import amplitude from '../../utils/ampli';
import PerformanceFilterDrawer from '../ui/performanceFilterDrawer';
import { cn, convertToJsonObject } from '../../utils/utils';
import { FilterIcon } from 'lucide-react/dist/cjs/lucide-react';
import { LayoutGrid, Table as TableIcon } from "lucide-react"; 
import { useLocation } from 'react-router-dom';
import { setSelectedCaratRange, setSelectedCell as setShapeCaratCell, setSelectedShape, selectShapeCaratState } from '../../redux/slices/shapeCaratSlice';
import { setSelectedClarities, setSelectedColors, setSelectedCell as setColorClarityCell } from '../../redux/slices/colorClaritySlice';
import DownloadNeeds from '../DownloadNeeds';
import { Tooltip } from '@mui/material';

const BIAnalysis = ({ isCsr, accountDetails }) => {
  const {
    selectedMetric,
    selectedNeedsMetric
  } = useSelector(selectShapeCaratState);
  const isLab = accountDetails.account_id == "1130";
  const [viewMode, setViewMode] = useState("grid");
  const filteredRedux = useSelector(pricing).filteredPerformance;
  const dispatch = useDispatch();
  const dateRange = useSelector(selectDateRange);
  const debouncedRefetch = debounce((callback) => callback(), 300);
  const [diamondKeys, setDiamondKeys] = useState([]);
  const [tableViewData, setTableViewData] = useState([]);
  const [gridDatas, setGridDatas] = useState([]);
  const [allCaratRanges, setAllCaratRanges] = useState([]);
  const [isChangingDate, setIsChangingDate] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const customFiltersRaw = useSelector(pricing)?.config?.custom_filters ?? [];
  const [filtered, setFiltered] = useState(filteredRedux);
  const {
      selectedShape,
      selectedCaratRange,
    } = useSelector(selectShapeCaratState);
  const {
      selectedColors,
      selectedClarities,
     
      selectedColorGroup,
      selectedClarityGroup,
      selectedGroupedCell,
    } = useSelector((state) => state.colorClarity);   
  const toggleDrawer = (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setOpenDrawer(!openDrawer);
  };

  const { 
    data, 
    error, 
    isLoading, 
    refetch,
    isFetching 
  } = useGetShapeSizeDistributionQuery({
    id: accountDetails.account_id,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    filters: convertToJsonObject(filteredRedux, customFiltersRaw, isLab)
  });

  const { 
    data: dataTable, 
    error: errorTable, 
    isLoading: isLoadingTable, 
    refetch: refetchTable,
    isFetching: isFetchingTable 
  } = useGetTableDistributionQuery({
    id: accountDetails.account_id,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    filters: convertToJsonObject(filteredRedux, customFiltersRaw, isLab)
  }, {
    skip: viewMode !== "table" // Only execute when viewMode is "table"
  });


  // Handle data processing when API response changes
  useEffect(() => {
    if (data?.data) {
      const keys = Object.keys(data.data);
      setDiamondKeys(keys);
      const mappedData = Object.values(data.data).map(category =>
        Object.entries(category)
          .filter(([key]) => key !== 'trans')
          .map(([, value]) => value)
      );
      setGridDatas(mappedData);

      const caratRanges = Object.keys(data.data[keys[0]])
        .filter(caratRange => caratRange !== "trans");
      setAllCaratRanges(caratRanges);
    }
  }, [data, filteredRedux, filtered]);

const tableRows = useMemo(() => {
  if (!dataTable) return [];
  if (Array.isArray(dataTable)) return dataTable;
  if (Array.isArray(dataTable.data)) return dataTable?.data;
  return [];
}, [dataTable]);  

useEffect(() => {
  
  if (dataTable?.data) {
    setTableViewData(tableRows);
  }
}, [dataTable, filteredRedux,isLoadingTable, errorTable]);

  // Handle date range changes and refetch data
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      setIsChangingDate(true);
      refetch().finally(() => {
        setIsChangingDate(false);
      });
    }
  }, [dateRange, refetch]);

  const handleDateRangeChange = (newRange) => {
    dispatch(setDateRange({
      startDate: newRange.startDate,
      endDate: newRange.endDate
    }));
    const buttonClickedEvent = {
        event_type: `v3_stock_performance_datechange_${newRange}`,
        value: newRange,
      };
    amplitude.track(buttonClickedEvent);
    setIsChangingDate(true);
    debouncedRefetch(() => {
    refetch().finally(() => {
      setIsChangingDate(false);
    });
  });
  
  // If currently in table view, also refetch table data
  if (viewMode === "table") {
    refetchTable();
  }
  };
  const location = useLocation();

  
  useEffect(() => {
  const searchParams = new URLSearchParams(location.search);
  const shape = searchParams.get("sh");
  const caratRange = searchParams.get("cr");
  const color = searchParams.get("col");
  const clarity = searchParams.get("clr");
  const endDate = searchParams.get("ed");
  const startDate = searchParams.get("sd");

  if (startDate && endDate) {
    dispatch(setDateRange({
      startDate: startDate,
      endDate: endDate
    }));
  }

  if (shape && caratRange) {
    dispatch(setShapeCaratCell({ shape, caratRange }));
    dispatch(setSelectedShape(shape));
    dispatch(setSelectedCaratRange(caratRange));
  }

  if (color && clarity) {
    const colorList = color.split(",");
    const clarityList = clarity.split(",");
    const isGrouped = colorList.length > 1 || clarityList.length > 1;

    if (isGrouped) {
      dispatch(setColorClarityCell({
        col: colorList,
        clr: clarityList,
      }));
      dispatch(setSelectedColors(colorList));
      dispatch(setSelectedClarities(clarityList));
    } else {
      dispatch(setColorClarityCell({
        col: colorList[0],
        clr: clarityList[0],
      }));
      dispatch(setSelectedColors(colorList));
      dispatch(setSelectedClarities(clarityList));
    }
    // dispatch(setColorClarityCell({ color, clarity }));
    // dispatch(setSelectedColors([color]));
    // dispatch(setSelectedClarities([clarity]));
  }

  if (!location.search || !searchParams.toString()) {
    const url = new URL(window.location);

    if (selectedShape) url.searchParams.set("sh", selectedShape);
    if (selectedCaratRange) url.searchParams.set("cr", selectedCaratRange);

    if (selectedColors?.length)
      url.searchParams.set("col", selectedColors.join(","));

    if (selectedClarities?.length)
      url.searchParams.set("clr", selectedClarities.join(","));

    if (dateRange.startDate && dateRange.endDate) {
      url.searchParams.set("sd", dateRange.startDate);
      url.searchParams.set("ed", dateRange.endDate);
    }
    window.history.replaceState({}, "", url);
  }
}, [location.search]);

  // Show loading state for initial load or date changes
  const showLoading = isLoading || isChangingDate || isFetching;
  const showLoadingTable =  isLoadingTable || isFetchingTable || isChangingDate;
  return (
      <div className="px-2 py-6 ">
        <PerformanceFilterDrawer
          open={openDrawer}
          onClose={(ev) => toggleDrawer(ev)}
          filtered={filtered}
          setFiltered={(value) => {
            setFiltered(value);
          }}
          setIsResetting={setIsResetting}
          accountDetails={accountDetails.account_id}
        />
        <div className='w-full px-48 flex justify-end'>   
             
      </div>
      <div className="pagetitle text-center flex justify-center mx-auto">
        <h1 className='w-[675px] ml-12 text-right'>SHAPE CARAT DISTRIBUTION</h1>
        <div className='flex ml-[50px] gap-1 '>
        <div className="flex gap-1 mr-2 border rounded-md p-1">
          {/* Grid Button */}
          <Tooltip title="Grid View">
            <button
              onClick={() => setViewMode("grid")}
              className={cn(
                "rounded-md flex gap-1 items-center justify-center px-2 h-6 border font-medium text-xs",
                viewMode === "grid"
                  ? "bg-[#1E90FF] text-white border-[#1E90FF]"
                  : "bg-transparent text-[#1E90FF] border-[#1E90FF]"
              )}
            >
              <LayoutGrid className="h-3 w-3" />
            </button>
           </Tooltip>
            {/* Table Button */}
            <Tooltip title="Table View">
            <button
              onClick={() => setViewMode("table")}
              className={cn(
                "rounded-md flex gap-1 items-center justify-center px-2 h-6 border font-medium text-xs",
                viewMode === "table"
                  ? "bg-[#1E90FF] text-white border-[#1E90FF]"
                  : "bg-transparent text-[#1E90FF] border-[#1E90FF]"
              )}
            >
              <TableIcon className="h-3 w-3" />
            </button>
            </Tooltip>
          </div>
          <div className="flex  items-center">
            <DownloadNeeds isLab={isLab} accountDetails={accountDetails} selectedNeedsMetric={selectedMetric === "needs" ? selectedNeedsMetric : "all"} startDate={dateRange.startDate} endDate={dateRange.endDate} />
          </div>
          <button
                className={cn(
                  "self-end rounded-md flex gap-[0.200rem] items-center justify-center px-3 h-8 border-solid border-[1px] border-[#1E90FF] font-medium text-[0.75rem] w-max ",
                  filtered.length > 0
                    ? "bg-[#A4D9FF] text-[#1E90FF]"
                    : "bg-transparent text-[#1E90FF]"    
                )}
                onClick={(ev) => {
                  ev.stopPropagation();
                  toggleDrawer(ev);
                }}
              ><FilterIcon className=' h-3 w-3' /> Filters
          </button>
          
        </div>
      </div>
      
      <div className="mt-4">
        {showLoading ? (
          <div className="flex justify-center items-center h-20">
            <ColorRing
              visible={true}
              height="60"
              width="60"
              ariaLabel="blocks-loading"
              colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
            />
          </div>
        ) : data?.data ? (
              <ShapeCaratGrid
                isCsr={isCsr}
                isLab={isLab}
                accountDetails={accountDetails}
                caratRanges={allCaratRanges}
                setViewMode={setViewMode}
                viewMode={viewMode}
                tableViewData={tableViewData}
                showLoadingTable={showLoadingTable}
                shapes={diamondKeys}
                gridData={gridDatas}
                dateRange={dateRange}
                defaultTurn={data.ideal_turn}
                defaultInterest={data.interest_rate}
                metaSummary={data.meta}
                data={data.data}
                onDateRangeChange={handleDateRangeChange}
                isResetting={isResetting}
              />
            ) : (
              <div className="flex justify-center">
                No data available
              </div>
            )}

        
      </div>
    </div>
  );
};

export default BIAnalysis;